import type { Dispatch, SetStateAction } from 'react';
import { useEffect } from 'react';

import omit from 'lodash/omit';

import type {
    CaptionRestrictionsType,
    CaptionType,
    RestrictionType
} from '@/lib/captionsHashtagLists/captionRestrictions';
// Lib
import { hashtagRegex, mentionRegex } from '@/lib/regex';

export type ValidateReturnType = {
    valid: boolean;
    isMentionsValid?: boolean;
    isHashtagsValid?: boolean;
    isLengthValid?: boolean;
    remaining?: number;
    warning: boolean;
};

export const validateHashtags = (
    caption: CaptionType,
    restriction: RestrictionType,
    setState: Dispatch<SetStateAction<CaptionRestrictionsType[]>>
): ValidateReturnType => {
    const hashtags = caption?.match(hashtagRegex) ?? [];
    const hashLeft = restriction?.limit - hashtags?.length;

    if (typeof setState === 'function') {
        setState((prevState) => {
            return [
                ...prevState.map((item) =>
                    item?.type === 'hashtags'
                        ? { ...item, restriction: { ...item.restriction, limit: hashLeft } }
                        : item
                )
            ];
        });
    }

    return {
        valid: hashLeft >= 0,
        isHashtagsValid: hashLeft >= 0,
        remaining: hashLeft,
        warning: restriction?.warning ? hashLeft <= restriction?.warning && hashLeft >= 0 : false
    };
};

export const validateMentions = (
    caption: CaptionType,
    restriction: RestrictionType,
    setState: Dispatch<SetStateAction<CaptionRestrictionsType[]>>
): ValidateReturnType => {
    const mentions = caption?.match(mentionRegex) ?? [];
    const hashLeft = restriction?.limit - mentions?.length;

    if (typeof setState === 'function') {
        setState((prevState) => {
            return [
                ...prevState.map((item) =>
                    item?.type === 'mentions'
                        ? { ...item, restriction: { ...item.restriction, limit: hashLeft } }
                        : item
                )
            ];
        });
    }

    return {
        valid: hashLeft >= 0,
        isMentionsValid: hashLeft >= 0,
        remaining: hashLeft,
        warning: restriction?.warning ? hashLeft <= restriction?.warning && hashLeft >= 0 : false
    };
};

export const validateLength = (
    caption: CaptionType,
    restriction: RestrictionType,
    setState: Dispatch<SetStateAction<CaptionRestrictionsType[]>>
): ValidateReturnType => {
    const charLeft = restriction?.limit - caption?.length;
    if (typeof setState === 'function') {
        setState((prevState) => {
            return [
                ...prevState.map((item) =>
                    item?.type === 'characters'
                        ? { ...item, restriction: { ...item.restriction, limit: charLeft } }
                        : item
                )
            ];
        });
    }

    return {
        valid: charLeft >= 0,
        isLengthValid: charLeft >= 0,
        remaining: charLeft,
        warning: restriction?.warning ? charLeft <= restriction?.warning && charLeft >= 0 : false
    };
};

export const checkCaptionValidation = (
    caption: CaptionType,
    restrictions: CaptionRestrictionsType[],
    setState: Dispatch<SetStateAction<CaptionRestrictionsType[]>>
) => {
    if (!restrictions) return;
    const status = restrictions?.map(({ restriction, validate }) =>
        validate(caption, restriction, setState)
    );

    const isValid = status?.map((el) => el?.valid);
    const hasWarning = status?.map((el) => el?.warning);

    const additionalFields = omit<Omit<ValidateReturnType, 'valid' | 'warning'>>(
        status.reduce((obj, item) => Object.assign(obj, item), {}),
        ['valid', 'warning']
    );

    if (isValid.includes(false)) {
        return { valid: false, warning: false, ...additionalFields };
    } else if (!isValid.includes(false)) {
        if (hasWarning.includes(true)) {
            return { valid: true, warning: true, ...additionalFields };
        }
        return { valid: true, warning: false, ...additionalFields };
    }
};

export const useValidateCaption = (
    caption: CaptionType,
    restrictions: CaptionRestrictionsType[],
    callback: (arg: ValidateReturnType) => void,
    setState: Dispatch<SetStateAction<CaptionRestrictionsType[]>>
) => {
    useEffect(() => {
        const data = checkCaptionValidation(caption, restrictions, setState);
        if (data) {
            return callback(data);
        }
        return;
    }, [caption]);
};
